import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
} from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Store } from '@ngrx/store';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { UserState } from 'src/app/store/user/user.reducer';
import {
  UpdateNotificationPreferencesInput,
  UserDetails,
} from '../../login/user.model';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDivider } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { UserService } from 'src/app/services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { CommoditiesService } from 'src/app/services/commodities/commodities.service';
import { MarketType } from '../../bids-and-offers/lots.model';
import { Commodity } from 'src/app/services/commodities/commodity.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { selectUser } from 'src/app/store/user/user.selectors';
import { Analytics, logEvent } from '@angular/fire/analytics';
import { LogEventsEnum } from 'src/app/firebase.model';

@Component({
  selector: 'app-notification-preferences',
  imports: [
    CommonModule,
    MatExpansionModule,
    MatListModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatDivider,
    MatButtonModule,
    MatSelectModule,
  ],
  templateUrl: './notification-preferences.component.html',
  styleUrl: './notification-preferences.component.css',
})
export class NotificationPreferencesComponent implements OnInit {
  private analytics = inject(Analytics);
  private store = inject(Store<{ user: UserState }>);
  private fb = inject(FormBuilder);
  private userService = inject(UserService);
  private snackBar = inject(MatSnackBar);
  private commodityService = inject(CommoditiesService);
  destroyRef = inject(DestroyRef);
  topicValues = [
    { label: 'New Pending Contract', value: 'NewPendingContract' },
    { label: 'Announcements', value: 'Announcements' },
    // { label: 'Trader Messages', value: 'TraderMessage' },
    // { label: 'Daily', value: 'Daily' },
  ];
  userDetails$: Observable<UserDetails>;

  commodities = signal<Commodity[] | null>(null);

  emailPreferences = this.fb.group({
    emails: [false],
    preferences: this.fb.group({
      contracts: [false],
      bids: [false],
      commodities: this.fb.control<string[]>([]),
      offers: [false],
      negotiations: [false],
      trades: [false],
    }),
  });

  pushNotifications = this.fb.nonNullable.group({
    topics: this.fb.control<string[]>([]),
    bids: [false],
    offers: [false],
    trades: [false],
    negotiations: [false],
    contracts: [false],
    commodities: this.fb.control<string[]>([]),
  });
  topics = signal<string[]>(null);

  constructor() {}

  ngOnInit(): void {
    this.userDetails$ = this.store.select(selectUser).pipe(
      map((user) => {
        if (user.email_preferences !== null) {
          this.emailPreferences
            .get('preferences')
            .patchValue({ ...user.email_preferences });
          this.emailPreferences
            .get('emails')
            .patchValue(user.receive_email_notifications);
        }
        if (
          'notificationPreferences' in user &&
          user.notificationPreferences !== null
        ) {
          this.pushNotifications.patchValue({
            ...user.notificationPreferences,
          });
        }
        return user;
      }),
      tap((user) => {
        this.commodityService
          .watch({
            market: MarketType[user.default_market_type],
            origin: user.default_origin,
          })
          .valueChanges.subscribe((res) => {
            this.commodities.set(res.data.commodities);
          });
        return of(user);
      }),
      takeUntilDestroyed(this.destroyRef)
    );
  }

  toggleAllEmails(): void {
    if (this.emailPreferences.get('emails').value) {
      this.emailPreferences.get('preferences').enable();
    } else {
      this.emailPreferences.patchValue({
        preferences: {
          contracts: false,
          bids: false,
          offers: false,
          negotiations: false,
          trades: false,
        },
      });
      this.emailPreferences.get('preferences').disable();
    }
  }

  onSubmitEmailPrefs(): void {
    this.userService
      .updateUserEmailPreferences(
        this.emailPreferences.get('emails').value,
        this.emailPreferences.get('preferences').value
      )
      .subscribe({
        next: (res) => {
          this.snackBar.open('Saved email Preferences', 'Close', {
            duration: 3000,
            panelClass: ['blue-snackbar'],
          });
          this.emailPreferences.markAsPristine();
          logEvent(this.analytics, LogEventsEnum.user_update);
        },
        error: (err) => {
          this.snackBar.open(`Error Saving Email Preferences ${err}`, 'Close', {
            duration: 3000,
            panelClass: ['blue-snackbar'],
          });
        },
      });
  }

  onSubmitNotificationPrefs(): void {
    this.userService
      .updatePushNotificationPreferences({
        ...this.pushNotifications.value,
        topics: this.pushNotifications
          .get('topics')
          .value.filter((t) => t !== ''),
      } as UpdateNotificationPreferencesInput)
      .subscribe({
        next: (res) => {
          this.pushNotifications.markAsPristine();
          this.snackBar.open('Saved Push Notification Preferences', 'Close', {
            duration: 3000,
            panelClass: ['blue-snackbar'],
          });
          logEvent(this.analytics, LogEventsEnum.user_update);
        },
        error: (err) => {
          this.snackBar.open(`Error Saving Preferences ${err}`, 'Close', {
            duration: 3000,
            panelClass: ['blue-snackbar'],
          });
        },
      });
  }
}
